import { z } from 'zod';

/**
 * Firm (company) shapes for the admin firm-management surface (M13, ADR 0026). A firm is the
 * partner record an operator creates in the panel **and** the ingestion identity: its API
 * keys (`company_api_keys`) are what an `X-Api-Key` resolves to, and everything submitted
 * with one is owned by the firm rather than by a person.
 *
 * Mirrors the `companies` table (`docs/03-data-model.md`); timestamps are epoch-ms integers
 * on the wire. Nullable-and-optional fields follow the repo's patch convention: absent means
 * "leave alone", explicit `null` means "clear it".
 */

const optionalText = (max: number) => z.string().max(max).nullable().optional();

/** Full firm record — `GET /admin/companies` and `GET /admin/companies/:id`. */
export const companySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  editorial: z.boolean(),
  taxAdmin: z.string().nullable(), // vergi dairesi
  taxNumber: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  address: z.string().nullable(),
  createdAt: z.number().int(), // epoch-ms (0 for pre-M13 rows)
  archivedAt: z.number().int().nullable(), // epoch-ms soft archive
  /** Non-revoked ingestion keys the firm currently holds (list view only). */
  activeKeyCount: z.number().int().default(0),
});
export type Company = z.infer<typeof companySchema>;

/**
 * The picker projection (`CompanyPicker`, sale `targetCompany`, user `relatedCompany`). Kept
 * as a strict subset of {@link companySchema} so the same endpoint feeds both — zod strips
 * the extra keys when a caller parses with this one.
 */
export const companySummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type CompanySummary = z.infer<typeof companySummarySchema>;

export const companyCreateSchema = z.object({
  // Trimmed BEFORE the length check, and before it reaches the `UNIQUE(name)` index: that
  // index compares bytes, so `Acme ` and `Acme` would be two firms with one visible name in
  // every picker — and a whitespace-only string would pass `min(1)` as an unnameable firm.
  name: z.string().trim().min(1).max(160),
  editorial: z.boolean().default(false),
  taxAdmin: optionalText(120),
  taxNumber: optionalText(40),
  contactEmail: z.email().max(160).nullable().optional(),
  contactPhone: optionalText(40),
  address: optionalText(500),
});
export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;

/**
 * All fields optional; only provided fields are patched (`null` clears).
 *
 * `editorial` is re-declared rather than inherited: in Zod 4 a `.default()` inside a
 * `.partial()`ed object **still fires**, so `companyCreateSchema.partial()` would turn
 * `{ taxNumber: '9' }` into `{ taxNumber: '9', editorial: false }` — and a PATCH handler that
 * treats every defined value as intentional would then clear the flag on an editorial firm
 * every time an operator edited its phone number.
 */
export const companyUpdateSchema = companyCreateSchema
  .partial()
  .extend({ editorial: z.boolean().optional() });
export type CompanyUpdateInput = z.infer<typeof companyUpdateSchema>;
